import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Leaflet 기본 아이콘 번들 이슈 수정 (Vite + Webpack 환경)
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";

// @ts-expect-error leaflet internal
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

export const BUSAN_CENTER: [number, number] = [35.1796, 129.0756];
export const BUSAN_ZOOM = 11;
export const ORIGIN_COORDS: [number, number] = [35.1530, 128.9799]; // 사상구 하신번영로 440

/**
 * 지도 타일 옵션.
 * OSM 기본 · Kakao/Naver는 이후 JS SDK 로드시 교체 가능.
 */
export const TILE_LAYERS = {
  osm: {
    url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
    attribution: "© OpenStreetMap contributors",
    maxZoom: 19,
  },
  cartoLight: {
    url: "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png",
    attribution: "© OpenStreetMap © CARTO",
    maxZoom: 19,
  },
  cartoDark: {
    url: "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png",
    attribution: "© OpenStreetMap © CARTO",
    maxZoom: 19,
  },
};

/** 배송 상태별 색 */
export const STATUS_COLORS = {
  pending: "#94a3b8",   // 회색 (미배송)
  done: "#10b981",      // 초록 (완료)
  courier: "#f59e0b",   // 주황 (월말 택배)
  missing: "#ef4444",   // 빨강 (좌표 없음)
  mine: "#1f66ea",      // 파랑 (내가 담당)
  otherClaimed: "#cbd5e1", // 연회색 (타 기사)
};

/** 숫자 뱃지 아이콘 — 경로 번호 */
export function numberIcon(n: number, color: string = "#1f66ea"): L.DivIcon {
  return L.divIcon({
    className: "route-number-icon",
    html: `<div style="
      background:${color};color:white;width:28px;height:28px;border-radius:50%;
      display:flex;align-items:center;justify-content:center;font-weight:700;
      box-shadow:0 2px 6px rgba(0,0,0,.25);border:2px solid white;font-size:13px;
    ">${n}</div>`,
    iconSize: [28, 28],
    iconAnchor: [14, 14],
  });
}

/** 색 원 아이콘 — 상태 표시 */
export function dotIcon(color: string, size = 14): L.DivIcon {
  return L.divIcon({
    className: "delivery-dot-icon",
    html: `<div style="
      background:${color};width:${size}px;height:${size}px;border-radius:50%;
      border:2px solid white;box-shadow:0 1px 3px rgba(0,0,0,.25);
    "></div>`,
    iconSize: [size + 4, size + 4],
    iconAnchor: [(size + 4) / 2, (size + 4) / 2],
  });
}

/** 동 뱃지 — 가구 수 + 선택 여부 */
export function dongBadgeIcon(dong: string, count: number, state: "mine" | "other" | "none"): L.DivIcon {
  const bg = state === "mine" ? "#1f66ea" : state === "other" ? "#cbd5e1" : "white";
  const fg = state === "mine" ? "white" : "#0f172a";
  const border = state === "mine" ? "#1551c5" : "#94a3b8";
  return L.divIcon({
    className: "dong-badge-icon",
    html: `<div style="
      background:${bg};color:${fg};border:2px solid ${border};
      padding:4px 8px;border-radius:999px;font-size:12px;font-weight:700;
      white-space:nowrap;box-shadow:0 2px 8px rgba(0,0,0,.18);
      display:flex;gap:4px;align-items:center;
    "><span>${dong}</span><span style="opacity:.75;font-weight:500;">${count}</span></div>`,
    iconSize: [100, 24],
    iconAnchor: [50, 12],
  });
}

/** 학교 뱃지 — 학교명 + 가구 수 + 클레임 상태 */
export function schoolBadgeIcon(school: string, count: number, state: "mine" | "other" | "none"): L.DivIcon {
  const bg = state === "mine" ? "#1f66ea" : state === "other" ? "#cbd5e1" : "white";
  const fg = state === "mine" ? "white" : "#0f172a";
  const border = state === "mine" ? "#1551c5" : "#94a3b8";
  return L.divIcon({
    className: "school-badge-icon",
    html: `<div style="
      background:${bg};color:${fg};border:2px solid ${border};
      padding:4px 8px;border-radius:6px;font-size:12px;font-weight:700;
      white-space:nowrap;box-shadow:0 2px 8px rgba(0,0,0,.18);
      display:flex;gap:4px;align-items:center;
    "><span>🎒</span><span>${school}</span><span style="opacity:.75;font-weight:500;">${count}</span></div>`,
    iconSize: [140, 26],
    iconAnchor: [70, 13],
  });
}

/** 내 위치(탑차) 아이콘 — 배송기사 현재 위치 표시 */
export function truckIcon(): L.DivIcon {
  return L.divIcon({
    className: "truck-icon",
    html: `<div style="
      background:#0f172a;color:white;width:36px;height:36px;border-radius:50%;
      display:flex;align-items:center;justify-content:center;font-size:20px;
      border:3px solid #38bdf8;box-shadow:0 2px 8px rgba(0,0,0,.35);
    ">🚚</div>`,
    iconSize: [36, 36],
    iconAnchor: [18, 18],
  });
}

/** 출발지 아이콘 */
export function originIcon(): L.DivIcon {
  return L.divIcon({
    className: "origin-icon",
    html: `<div style="
      background:#111827;color:white;padding:4px 8px;border-radius:8px;
      font-weight:700;font-size:11px;border:2px solid white;
      box-shadow:0 2px 8px rgba(0,0,0,.3);
    ">🏭 창고</div>`,
    iconSize: [60, 24],
    iconAnchor: [30, 12],
  });
}
